import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, GitBranch, History, ListChecks, MessageCircleQuestion, Scale, ShieldAlert, TrendingUp } from "lucide-react";
import api, { apiError, ruleReasons } from "../lib/api";
import { userName, useUsers } from "../lib/hooks";
import { Chip, PageHeader, ReasonsList, ToneChip } from "../components/common";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { COMMITMENT_STATUS, DECISION_EXEC_STATUS, DEP_KIND, MEETING_STATUS, OPEN_QUESTION_STATUS, TASK_STATUS, fmtDate, fmtDateTime } from "../lib/labels";

const TASK_ACTION_LABELS = {
  iniciar: "Iniciar", aportar_evidencia: "Aportar evidencia", enviar_revision: "Enviar a revisión",
  aprobar_revision: "Aprobar revisión", devolver: "Devolver", reanudar: "Reanudar",
  esperando_tercero: "Esperando tercero", bloquear: "Bloquear", desbloquear: "Desbloquear",
  completar: "Completar", cancelar: "Cancelar",
};
const DEC_ACTION_LABELS = {
  iniciar_ejecucion: "Iniciar ejecución", registrar_ejecucion: "Registrar ejecución", verificar: "Verificar",
  bloquear: "Bloquear", desbloquear: "Desbloquear", sustituir: "Sustituir", cancelar: "Cancelar",
};
const Q_ACTION_LABELS = { asignar: "Asignar", pedir_info: "Pedir información", responder: "Responder", validar: "Validar respuesta", solicitar_ampliacion: "Solicitar ampliación", cerrar: "Cerrar" };
const C_ACTION_LABELS = {
  validar: "Validar", marcar_pendiente: "Marcar pendiente", registrar_cumplimiento: "Registrar cumplimiento",
  marcar_incumplido: "Marcar incumplido", enviar_revision_legal: "Enviar a Legal", clasificar_no_contractual: "No contractual",
  convertir_obligacion: "Convertir en obligación",
};

function OverdueChip({ days }) {
  if (!days || days <= 0) return null;
  return <ToneChip label={`${days}d de retraso`} tone="danger" />;
}

export default function ReunionSeguimiento() {
  const { mid } = useParams();
  const { users, map } = useUsers();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState(null); // {kind: task|dec|proc|question|commitment, action, item}
  const [fields, setFields] = useState({});
  const [err, setErr] = useState(null);
  const [proposal, setProposal] = useState(null);

  const { data, error } = useQuery({ queryKey: ["seguimiento", mid], queryFn: async () => (await api.get(`/reuniones/${mid}/seguimiento`)).data, retry: false });
  const { data: meta } = useQuery({ queryKey: ["seguimiento-meta"], queryFn: async () => (await api.get("/reuniones/seguimiento/meta")).data });

  const refresh = () => qc.invalidateQueries({ queryKey: ["seguimiento", mid] });
  const mut = useMutation({
    mutationFn: async ({ path, body }) => (await api.post(path, body || {})).data,
    onSuccess: (res) => {
      if (res && res.idempotent_replay) toast.info("El proceso ya existía: no se duplica (idempotente)");
      else toast.success("Operación aplicada");
      setDialog(null); setProposal(null); setFields({}); setErr(null); refresh();
    },
    onError: (e) => { setErr(e); toast.error(apiError(e) || "Error al aplicar la operación"); },
  });

  const openDlg = (d) => { setDialog(d); setFields({}); setErr(null); setProposal(null); };
  const needs = dialog ? ((dialog.meta || []).find((a) => a.action === dialog.action)?.needs || []) : [];

  if (error) {
    return (
      <div className="p-8" data-testid="seguimiento-denied">
        <p className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">El seguimiento de reuniones es interno: {apiError(error)}</p>
      </div>
    );
  }
  if (!data) return <div className="p-8 text-sm text-slate-500">Cargando…</div>;
  const m = data.meeting;

  const submit = () => {
    const f = { ...fields };
    if (dialog.kind === "task") mut.mutate({ path: `/reuniones/${mid}/acciones/${dialog.item.id}/accion`, body: { action: dialog.action, ...f } });
    else if (dialog.kind === "dec") mut.mutate({ path: `/reuniones/decisiones/${dialog.item.id}/ejecucion`, body: { action: dialog.action, ...f } });
    else if (dialog.kind === "proc") mut.mutate({ path: `/reuniones/decisiones/${dialog.item.id}/procesos`, body: { kind: f.procKind, confirm: !!f.confirm, title: f.title, description: f.description, estimated_amount: f.estimated_amount ? Number(f.estimated_amount) : undefined, contract_id: f.contract_id || undefined, responsible_id: f.responsible_id || undefined, legal_responsible_id: f.legal_responsible_id || undefined, due_date: f.due_date || undefined, severity: f.severity || undefined } });
    else if (dialog.kind === "question") mut.mutate({ path: `/reuniones/cuestiones/${dialog.item.id}/accion`, body: { action: dialog.action, ...f } });
    else if (dialog.kind === "commitment") mut.mutate({ path: `/reuniones/compromisos/${dialog.item.id}/accion`, body: { action: dialog.action, ...f } });
  };

  return (
    <div className="space-y-4" data-testid="seguimiento-page">
      <Link to="/reuniones" className="flex items-center gap-1 text-xs text-blue-700 hover:underline" data-testid="back-to-meetings">
        <ArrowLeft className="h-3 w-3" /> Volver a reuniones
      </Link>
      <PageHeader
        title={`Seguimiento — ${m.title}`}
        subtitle="El cierre de la reunión no completa sus acciones: cada elemento mantiene estado, responsable, fecha y próxima acción hasta su cierre controlado."
        testid="seguimiento-title"
        actions={<div className="flex items-center gap-2">
          <Chip map={MEETING_STATUS} value={m.status} testid="meeting-status" />
          <Link to={`/reuniones/${mid}/acta`} className="text-xs font-medium text-blue-700 hover:underline" data-testid="open-minute">Acta →</Link>
        </div>}
      />

      {m.close_snapshot && (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="close-snapshot">
          Cierre {fmtDateTime(m.close_snapshot.closed_at)}: {m.close_snapshot.acciones_abiertas} acciones abiertas · {m.close_snapshot.decisiones_abiertas} decisiones en curso · {m.close_snapshot.cuestiones_abiertas} cuestiones · {m.close_snapshot.compromisos_abiertos} compromisos. {m.close_snapshot.note}
        </div>
      )}
      {m.reopen_pending && <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="reopen-pending">Reapertura excepcional pendiente de aprobación.</div>}

      {/* Próxima reunión y arrastrados */}
      {data.next_meeting && (
        <div className="rounded-md border border-blue-200 bg-blue-50/50 p-3" data-testid="next-meeting-panel">
          <p className="text-sm font-semibold text-blue-900"><History className="mr-1 inline h-4 w-4" />Próxima reunión: {data.next_meeting.title} ({fmtDate(data.next_meeting.date)}) — <Chip map={MEETING_STATUS} value={data.next_meeting.status} /></p>
          <div className="mt-2 space-y-1">
            {data.next_meeting.carried.map((c) => (
              <div key={c.id} className="flex items-center gap-2 rounded border border-blue-100 bg-white px-2 py-1.5 text-xs" data-testid={`carried-${c.carried_ref}`}>
                <ToneChip label="ARRASTRADO" tone="warning" />
                <span className="min-w-0 flex-1 truncate">{c.title}</span>
                <span className="text-slate-400">{userName(map, c.owner_id)} · {c.carried_count}ª vez</span>
              </div>
            ))}
            {!data.next_meeting.carried.length && <p className="text-xs text-slate-400">Sin pendientes arrastrados.</p>}
          </div>
        </div>
      )}

      {/* Escalaciones */}
      {data.escalations.length > 0 && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3" data-testid="escalations-panel">
          <p className="text-sm font-semibold text-red-900"><ShieldAlert className="mr-1 inline h-4 w-4" />Escalaciones activas</p>
          {data.escalations.map((e) => (
            <div key={e.id} className="mt-1.5 rounded border border-red-100 bg-white px-2 py-1.5 text-xs" data-testid={`escalation-${e.id}`}>
              <span className="font-medium">{e.task_id}</span> · {e.days_overdue} días de retraso · responsable {userName(map, e.responsible_id)} · escalado a {userName(map, e.escalated_to)} · impacto: {e.impact}
            </div>
          ))}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Acciones */}
        <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="tasks-panel">
          <p className="text-sm font-semibold text-slate-800"><ListChecks className="mr-1 inline h-4 w-4" />Acciones ({data.tasks.length})</p>
          {data.tasks.map((t) => (
            <div key={t.id} className={`mt-2 rounded border px-2 py-2 text-xs ${t.days_overdue > 0 ? "border-red-200 bg-red-50/40" : "border-slate-100"}`} data-testid={`task-${t.id}`}>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="font-medium text-slate-800">{t.title}</span>
                <Chip map={TASK_STATUS} value={t.status} testid={`task-status-${t.id}`} />
                <OverdueChip days={t.days_overdue} />
                {(t.carried_count || 0) > 0 && <ToneChip label={`arrastrada ${t.carried_count}×`} tone="warning" />}
                {t.requires_evidence && <ToneChip label="evidencia" tone="ai" />}
              </div>
              <p className="mt-1 text-slate-500">{userName(map, t.assignee_id)} · vence {fmtDate(t.due_date)} · esperado: {t.expected_result || "—"}</p>
              {t.result_text && <p className="text-emerald-700">Resultado: {t.result_text}</p>}
              {t.source_fragment && <p className="text-slate-400">Fuente: t={t.source_fragment.timestamp} · {t.source_fragment.speaker} · acta {t.minute_version || "—"}</p>}
              {t.blockers?.length > 0 && <p className="text-red-600">Bloqueo: {t.blockers.join("; ")}</p>}
              <div className="mt-1.5 flex flex-wrap gap-1">
                {(t.actions || []).filter((a) => a.allowed).map((a) => (
                  <Button key={a.action} size="sm" variant={a.action === "completar" ? "default" : "ghost"} className="h-6 px-2 text-xs"
                    data-testid={`task-${a.action}-${t.id}`} onClick={() => openDlg({ kind: "task", action: a.action, item: t, meta: t.actions })}>
                    {TASK_ACTION_LABELS[a.action] || a.action}
                  </Button>
                ))}
              </div>
              {(t.actions || []).filter((a) => !a.allowed && a.action === "completar" && a.reasons.length).map((a) => (
                <div key={a.action} className="mt-1" data-testid={`task-blocked-${t.id}`}><ReasonsList reasons={a.reasons} /></div>
              ))}
            </div>
          ))}
          {!data.tasks.length && <p className="mt-1 text-xs text-slate-400">Sin acciones materializadas desde esta reunión.</p>}
        </div>

        {/* Decisiones */}
        <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="decisions-panel">
          <p className="text-sm font-semibold text-slate-800"><GitBranch className="mr-1 inline h-4 w-4" />Decisiones y procesos dependientes ({data.decisions.length})</p>
          {data.decisions.map((d) => (
            <div key={d.id} className="mt-2 rounded border border-slate-100 px-2 py-2 text-xs" data-testid={`decision-${d.id}`}>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="font-medium text-slate-800">{d.title}</span>
                <Chip map={DECISION_EXEC_STATUS} value={d.exec_status} testid={`decision-status-${d.id}`} />
              </div>
              <p className="mt-1 text-slate-500">Responsable: {userName(map, d.exec_responsible_id)} · {d.impact ? `impacto: ${d.impact}` : "impacto por determinar"}</p>
              {d.exec_result && <p className="text-emerald-700">Resultado: {d.exec_result} {d.exec_verified_by ? `(verificada por ${userName(map, d.exec_verified_by)})` : "(pendiente de verificación)"}</p>}
              {(d.procs || []).map((p) => (
                <div key={p.id} className="mt-1 flex items-center gap-1.5 rounded bg-slate-50 px-2 py-1" data-testid={`proc-${p.id}`}>
                  <ToneChip label={DEP_KIND[p.kind] || p.kind} tone="info" />
                  <span className="min-w-0 flex-1 truncate">{p.title}</span>
                  <ToneChip label={p.status} tone={p.status === "completado" ? "success" : "warning"} />
                  {p.link && <Link to={p.link} className="text-blue-700 underline" data-testid={`proc-link-${p.id}`}>abrir</Link>}
                </div>
              ))}
              <div className="mt-1.5 flex flex-wrap gap-1">
                <Button size="sm" variant="outline" className="h-6 px-2 text-xs" data-testid={`dec-proponer-${d.id}`}
                  onClick={() => openDlg({ kind: "proc", item: d })}>Proponer proceso…</Button>
                {(d.actions || []).filter((a) => a.allowed).map((a) => (
                  <Button key={a.action} size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`dec-${a.action}-${d.id}`}
                    onClick={() => openDlg({ kind: "dec", action: a.action, item: d, meta: d.actions })}>
                    {DEC_ACTION_LABELS[a.action] || a.action}
                  </Button>
                ))}
              </div>
              {(d.actions || []).filter((a) => !a.allowed && a.permission && a.reasons.length && !a.reasons.some((r) => r.code === "estado")).map((a) => (
                <div key={a.action} className="mt-1" data-testid={`dec-blocked-${a.action}-${d.id}`}>
                  <p className="text-xs font-medium text-red-700">{DEC_ACTION_LABELS[a.action]} bloqueada:</p>
                  <ReasonsList reasons={a.reasons} />
                </div>
              ))}
            </div>
          ))}
          {!data.decisions.length && <p className="mt-1 text-xs text-slate-400">Sin decisiones vinculadas a esta reunión.</p>}
        </div>

        {/* Cuestiones */}
        <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="questions-panel">
          <p className="text-sm font-semibold text-slate-800"><MessageCircleQuestion className="mr-1 inline h-4 w-4" />Cuestiones abiertas ({data.questions.filter((q) => q.status !== "cerrada").length})</p>
          {data.questions.map((q) => (
            <div key={q.id} className={`mt-2 rounded border px-2 py-2 text-xs ${q.days_overdue > 0 ? "border-red-200 bg-red-50/40" : "border-slate-100"}`} data-testid={`question-${q.id}`}>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="font-medium text-slate-800">{q.question}</span>
                <Chip map={OPEN_QUESTION_STATUS} value={q.status} testid={`question-status-${q.id}`} />
                <OverdueChip days={q.days_overdue} />
              </div>
              <p className="mt-1 text-slate-500">Responsable: {userName(map, q.responsible_id)} · revisor: {userName(map, q.reviewer_id)} · {fmtDate(q.due_date)}</p>
              {q.answer && <p className="mt-1 text-slate-700">Respuesta: {q.answer} {q.answer_sources?.length ? `(fuentes: ${q.answer_sources.join(", ")})` : ""}</p>}
              {q.info_request && <p className="text-amber-700">Se requiere: {q.info_request}</p>}
              <div className="mt-1.5 flex flex-wrap gap-1">
                {(q.actions || []).filter((a) => a.allowed).map((a) => (
                  <Button key={a.action} size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`q-${a.action}-${q.id}`}
                    onClick={() => openDlg({ kind: "question", action: a.action, item: q, meta: q.actions })}>
                    {Q_ACTION_LABELS[a.action] || a.action}
                  </Button>
                ))}
              </div>
            </div>
          ))}
          {!data.questions.length && <p className="mt-1 text-xs text-slate-400">Sin cuestiones abiertas.</p>}
        </div>

        {/* Compromisos */}
        <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="commitments-panel">
          <p className="text-sm font-semibold text-slate-800"><Scale className="mr-1 inline h-4 w-4" />Compromisos ({data.commitments.length})</p>
          {data.commitments.map((c) => (
            <div key={c.id} className="mt-2 rounded border border-slate-100 px-2 py-2 text-xs" data-testid={`commitment-${c.id}`}>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="font-medium text-slate-800">{c.text}</span>
                <Chip map={COMMITMENT_STATUS} value={c.status} testid={`commitment-status-${c.id}`} />
              </div>
              <p className="mt-1 text-amber-700">{c.legal_warning}</p>
              <p className="text-slate-500">Formula: {c.formulated_by || "—"} · seguimiento: {userName(map, c.follow_up_responsible_id || c.responsible_id)} · {c.due_date ? fmtDate(c.due_date) : "sin fecha"} · evidencia esperada: {c.expected_evidence || "—"}</p>
              {c.fulfillment_evidence && <p className="text-emerald-700">Cumplido: {c.fulfillment_evidence}</p>}
              {c.breach_reason && <p className="text-red-600">Incumplido: {c.breach_reason}</p>}
              {c.obligation_id && <p className="text-blue-700">Obligación contractual creada: {c.obligation_id} (nace como candidata)</p>}
              <div className="mt-1.5 flex flex-wrap gap-1">
                {(c.actions || []).filter((a) => a.allowed).map((a) => (
                  <Button key={a.action} size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`c-${a.action}-${c.id}`}
                    onClick={() => openDlg({ kind: "commitment", action: a.action, item: c, meta: c.actions })}>
                    {C_ACTION_LABELS[a.action] || a.action}
                  </Button>
                ))}
              </div>
            </div>
          ))}
          {!data.commitments.length && <p className="mt-1 text-xs text-slate-400">Sin compromisos registrados.</p>}
        </div>
      </div>

      {/* Evidencias */}
      {data.evidence_requirements.length > 0 && (
        <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="evidence-panel">
          <p className="text-sm font-semibold text-slate-800">Evidencias de acciones</p>
          {data.evidence_requirements.map((r) => (
            <div key={r.id} className="mt-1.5 flex items-center gap-2 rounded border border-slate-100 px-2 py-1.5 text-xs" data-testid={`evreq-${r.id}`}>
              <span className="min-w-0 flex-1 truncate">{r.name}</span>
              <span className="text-slate-400">{userName(map, r.responsible_id)} · revisa {userName(map, r.reviewer_id)}</span>
              <ToneChip label={r.status} tone={r.status === "aceptada" ? "success" : r.status === "rechazada" ? "danger" : "warning"} />
            </div>
          ))}
        </div>
      )}

      {/* Diálogo genérico */}
      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent data-testid="seguimiento-dialog">
          <DialogHeader><DialogTitle>
            {dialog?.kind === "task" && (TASK_ACTION_LABELS[dialog.action] || dialog.action)}
            {dialog?.kind === "dec" && (DEC_ACTION_LABELS[dialog.action] || dialog.action)}
            {dialog?.kind === "question" && (Q_ACTION_LABELS[dialog.action] || dialog.action)}
            {dialog?.kind === "commitment" && (C_ACTION_LABELS[dialog.action] || dialog.action)}
            {dialog?.kind === "proc" && "Proponer proceso dependiente"}
          </DialogTitle></DialogHeader>
          {dialog && (
            <div className="space-y-3">
              {dialog.kind === "proc" && (
                <>
                  <div><Label>Tipo de proceso *</Label>
                    <Select value={fields.procKind || ""} onValueChange={async (v) => {
                      setFields({ ...fields, procKind: v });
                      setProposal((await api.get(`/reuniones/decisiones/${dialog.item.id}/procesos/proponer`, { params: { kind: v } })).data);
                    }}>
                      <SelectTrigger data-testid="field-procKind"><SelectValue placeholder="Selecciona proceso" /></SelectTrigger>
                      <SelectContent>{Object.entries(DEP_KIND).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {proposal && (
                    <div className="rounded-md border border-blue-200 bg-blue-50/60 p-3 text-xs" data-testid="proc-preview">
                      <p><strong>Decisión origen:</strong> {proposal.decision.title}</p>
                      <p className="mt-1"><strong>Proceso propuesto:</strong> {proposal.kind_label} · <strong>responsable:</strong> {userName(map, proposal.responsible_id)}</p>
                      <p className="mt-1"><strong>Datos transferidos:</strong> título, descripción, reunión {proposal.data_to_transfer.meeting_id}, decisión {proposal.data_to_transfer.decision_id}{proposal.data_to_transfer.project_id ? `, proyecto ${proposal.data_to_transfer.project_id}` : ""}</p>
                      <p className="mt-1"><strong>Impacto:</strong> {proposal.impact}</p>
                      <p className="mt-1 text-blue-800"><strong>Reglas del proceso destino:</strong> {proposal.destination_rules}</p>
                      {proposal.approval_required && <p className="mt-1 text-amber-700">Requiere aprobación en el proceso destino: la decisión no lo aprueba ni modifica presupuesto/planning.</p>}
                      <div className="mt-2"><Label>Título</Label><Input value={fields.title || proposal.data_to_transfer.title} onChange={(e) => setFields({ ...fields, title: e.target.value })} data-testid="field-title" /></div>
                      {fields.procKind === "purchase_request" && <div className="mt-2"><Label>Importe estimado (€)</Label><Input type="number" value={fields.estimated_amount || ""} onChange={(e) => setFields({ ...fields, estimated_amount: e.target.value })} data-testid="field-amount" /></div>}
                      {fields.procKind === "contract_review" && <div className="mt-2"><Label>Contrato (ID)</Label><Input value={fields.contract_id || proposal.data_to_transfer.linked_entity?.entity_id || ""} onChange={(e) => setFields({ ...fields, contract_id: e.target.value })} data-testid="field-contract" /></div>}
                      <label className="mt-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={!!fields.confirm} onChange={(e) => setFields({ ...fields, confirm: e.target.checked })} data-testid="field-confirm" /> Confirmo la activación del proceso (acción humana obligatoria)</label>
                    </div>
                  )}
                </>
              )}
              {needs.includes("motivo") && <div><Label>Motivo *</Label><Textarea value={fields.motivo || ""} onChange={(e) => setFields({ ...fields, motivo: e.target.value })} data-testid="field-motivo" /></div>}
              {needs.includes("comment") && <div><Label>Comentario *</Label><Textarea value={fields.comment || ""} onChange={(e) => setFields({ ...fields, comment: e.target.value })} data-testid="field-comment" /></div>}
              {needs.includes("evidence_text") && <div><Label>Evidencia aportada *</Label><Textarea value={fields.evidence_text || ""} onChange={(e) => setFields({ ...fields, evidence_text: e.target.value })} data-testid="field-evidence" /></div>}
              {needs.includes("tercero") && <div><Label>Tercero del que depende *</Label><Input value={fields.tercero || ""} onChange={(e) => setFields({ ...fields, tercero: e.target.value })} data-testid="field-tercero" /></div>}
              {dialog.kind === "task" && dialog.action === "completar" && dialog.item.expected_result && (
                <div><Label>Resultado acreditado * (esperado: {dialog.item.expected_result})</Label><Textarea value={fields.resultado || ""} onChange={(e) => setFields({ ...fields, resultado: e.target.value })} data-testid="field-resultado" /></div>
              )}
              {needs.includes("resultado") && <div><Label>Resultado de la ejecución *</Label><Textarea value={fields.resultado || ""} onChange={(e) => setFields({ ...fields, resultado: e.target.value })} data-testid="field-resultado" /></div>}
              {needs.includes("evidencia") && <div><Label>Evidencia *</Label><Textarea value={fields.evidencia || ""} onChange={(e) => setFields({ ...fields, evidencia: e.target.value })} data-testid="field-evidencia" /></div>}
              {needs.includes("nueva_decision") && <div><Label>Nueva decisión que la sustituye *</Label><Textarea value={fields.nueva_decision || ""} onChange={(e) => setFields({ ...fields, nueva_decision: e.target.value })} data-testid="field-nueva" /></div>}
              {needs.includes("responsible_id") && (
                <div><Label>Responsable *</Label>
                  <Select value={fields.responsible_id || ""} onValueChange={(v) => setFields({ ...fields, responsible_id: v })}>
                    <SelectTrigger data-testid="field-responsible_id"><SelectValue placeholder="Usuario" /></SelectTrigger>
                    <SelectContent>{(users || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {needs.includes("legal_responsible_id") && (
                <div><Label>Responsable Legal *</Label>
                  <Select value={fields.legal_responsible_id || ""} onValueChange={(v) => setFields({ ...fields, legal_responsible_id: v })}>
                    <SelectTrigger data-testid="field-legal"><SelectValue placeholder="Legal" /></SelectTrigger>
                    <SelectContent>{(users || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {needs.includes("answer") && <div><Label>Respuesta *</Label><Textarea value={fields.answer || ""} onChange={(e) => setFields({ ...fields, answer: e.target.value })} data-testid="field-answer" /></div>}
              {needs.includes("due_date") && <div><Label>Fecha compromiso *</Label><Input type="date" value={fields.due_date || ""} onChange={(e) => setFields({ ...fields, due_date: e.target.value })} data-testid="field-due_date" /></div>}
              {needs.includes("contract_id") && <div><Label>Contrato (ID) *</Label><Input value={fields.contract_id || dialog.item.linked_contract_id || ""} onChange={(e) => setFields({ ...fields, contract_id: e.target.value })} data-testid="field-contract" /></div>}
              {needs.includes("texto") && <div><Label>Texto de la obligación *</Label><Textarea value={fields.texto || ""} onChange={(e) => setFields({ ...fields, texto: e.target.value })} data-testid="field-texto" /></div>}
              {needs.includes("confirm") && (
                <label className="flex items-center gap-2 text-sm" data-testid="field-confirm-wrap">
                  <input type="checkbox" checked={!!fields.confirm} onChange={(e) => setFields({ ...fields, confirm: e.target.checked })} data-testid="field-confirm" />
                  Confirmo esta acción (acción humana obligatoria)
                </label>
              )}
              {err && (ruleReasons(err) ? <ReasonsList reasons={ruleReasons(err)} /> : <p className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700" data-testid="seguimiento-error">{apiError(err)}</p>)}
              {dialog.kind !== "proc" || proposal ? (
                <Button data-testid="seguimiento-dialog-submit" disabled={mut.isPending || (dialog.kind === "proc" && !proposal)} onClick={submit}>Confirmar</Button>
              ) : null}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
